'use client'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { X, Crown, MoreVertical } from 'lucide-react'
import { TIER_FEATURES, isProfessionalTier } from '@/lib/tier-features'
import { useGetStripePricesQuery } from 'graphql/generated/graphql'
import Image from 'next/image'
import { useState } from 'react'

interface AccountManagementModalProps {
  open: boolean
  onClose: () => void
  membershipType: string | null | undefined
  enhanced?: boolean | null
  subscriptionData?: {
    interval?: 'month' | 'year'
    nextPaymentAmount?: number
    nextPaymentDate?: string | number
  }
  onUpgrade?: () => void
  onManageSubscription?: () => void
  onCancelSubscription?: () => void
}

export function AccountManagementModal({
  open,
  onClose,
  membershipType,
  enhanced,
  subscriptionData,
  onUpgrade,
  onManageSubscription,
  onCancelSubscription,
}: AccountManagementModalProps) {
  const isPro = isProfessionalTier(membershipType, enhanced)
  const [showMenu, setShowMenu] = useState(false)
  const { data: pricingData } = useGetStripePricesQuery({ fetchPolicy: 'no-cache' })

  const stripeMonthlyPrice = pricingData?.stripePrices?.find(
    (p) => p.interval === 'month' && p.active
  )?.unitAmount
  const monthlyPrice = stripeMonthlyPrice ? stripeMonthlyPrice / 100 : 15.00

  const formatPaymentDate = (date: string | number | undefined) => {
    if (!date) return 'N/A'
    const dateObj = typeof date === 'number' ? new Date(date * 1000) : new Date(date)
    return dateObj.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
  }

  const hasPaymentMethod = !!subscriptionData?.nextPaymentAmount

  const getSubtitleText = () => {
    if (isPro) {
      const interval = subscriptionData?.interval || 'month'
      const amount = subscriptionData?.nextPaymentAmount || 15.00
      const date = formatPaymentDate(subscriptionData?.nextPaymentDate)
      const isMonthly = interval === 'month'

      return (
        <>
          <div>
            You are on the <span className="font-bold">Professional Plan</span>
            <br />
            and have elected to pay {isMonthly ? 'monthly' : 'yearly'}
          </div>
          <div className="mt-3">
            Your next payment of <span className="font-bold">${amount.toFixed(2)}</span>
            <br />
            will automatically process on <span className="font-bold">{date}</span>
          </div>
          {isMonthly && (
            <div className="mt-6 italic">
              Save 20% by selecting annual payments!
            </div>
          )}
        </>
      )
    }
    return 'You are currently on the free Verified business plan. Upgrade to access all features.'
  }

  const renderFeatureIcon = (value: boolean | string) => {
    if (value === true || (typeof value === 'string' && value !== 'false')) {
      return (
        <div className="w-4 h-4 relative bg-[#85c349] rounded-full flex items-center justify-center">
          <svg width="10" height="8" viewBox="0 0 10 8" fill="none" className="relative">
            <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      )
    }
    return (
      <div className="w-4 h-4 relative bg-[#d3d3d3] rounded-full flex items-center justify-center">
        <div className="w-2 h-[2px] bg-white rounded-sm"></div>
      </div>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[1118px] max-h-[85vh] bg-white rounded-3xl shadow-[0px_0px_40px_0px_rgba(0,0,0,0.30)] flex flex-col overflow-hidden p-0 border-none [&>button]:hidden">
        <DialogTitle className="sr-only">Account Management</DialogTitle>
        {/* Header - Logo and Close Button */}
        <div className="w-full px-6 lg:px-10 py-4 flex justify-between items-center border-b border-gray-100 bg-white z-30 shrink-0">
          <div className="w-[120px] lg:w-[138px]">
            <Image
              src="/images/logo.svg"
              alt="Culineer"
              width={138}
              height={48}
              style={{ width: 'auto', height: 'auto' }}
              className="w-full h-auto object-contain"
            />
          </div>
          <button
            onClick={onClose}
            className="bg-[#ececec] rounded-full p-1 text-neutral-500 hover:text-neutral-700 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Scrollable Content Area */}
        <div className="flex flex-col lg:flex-row lg:items-start overflow-y-auto flex-1 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full">
          {/* Left Side - Content with button positioned in middle */}
          <div className="w-full lg:w-[370px] px-6 lg:px-10 pb-6 lg:pb-8 flex flex-col gap-6 lg:gap-8 flex-shrink-0">
            {/* Title & Description */}
            <div className="flex flex-col gap-4 lg:gap-5">
              <div className="text-[#1a1a1a] text-2xl lg:text-3xl font-bold font-['Inter']">Your Culineer Plan</div>
              <div className="flex flex-col gap-3">
                <div className="text-[#2d3748] text-sm lg:text-base font-normal font-['Inter']">
                  {getSubtitleText()}
                </div>
              </div>
            </div>

            {/* Spacer to align button bottom with blue horizontal line */}
            <div className="hidden lg:block lg:flex-1" />

            {/* Button with ellipsis menu inline */}
            <div className="flex items-center gap-2 w-full mt-4 lg:mt-0">
              {isPro ? (
                <>
                  <button
                    onClick={hasPaymentMethod ? onManageSubscription : onUpgrade}
                    className="flex-1 px-4 py-2.5 bg-[#85c349] rounded-[999px] justify-center items-center gap-2 inline-flex hover:bg-[#75b339] transition-colors"
                  >
                    <Crown className="w-4 h-4 text-white" />
                    <div className="text-white text-sm font-semibold font-['Inter']">
                      {hasPaymentMethod ? 'Update payment' : 'Upgrade to Professional'}
                    </div>
                  </button>
                  {hasPaymentMethod && (
                    <div className="relative">
                      <button
                        onClick={() => setShowMenu(!showMenu)}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                      >
                        <MoreVertical className="h-5 w-5 text-[#2d3748]" />
                      </button>
                      {showMenu && (
                        <div className="absolute right-0 bottom-12 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-30 min-w-max">
                          <button
                            onClick={() => {
                              setShowMenu(false)
                              onCancelSubscription?.()
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-50 transition-colors whitespace-nowrap"
                          >
                            Cancel subscription
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <button
                  onClick={onUpgrade}
                  className="w-full px-5 py-2.5 bg-[#85c349] rounded-[999px] justify-center items-center gap-2 inline-flex hover:bg-[#75b339] transition-colors"
                >
                  <Crown className="w-4 h-4 text-white" />
                  <div className="text-white text-sm font-semibold font-['Inter']">Upgrade to Professional</div>
                </button>
              )}
            </div>
          </div>

          {/* Right Side - Features & Tiers */}
          <div className="w-full lg:flex-1 px-4 lg:pl-4 lg:pr-12 pb-6 lg:pt-0 lg:pb-4 bg-white relative overflow-x-auto [&::-webkit-scrollbar]:h-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full">
            <div className="min-w-[500px] lg:min-w-0 relative pb-4">
              {/* Green overlay for highlighted column - behind content */}
              <div className={`absolute top-0 w-24 lg:w-36 h-full bg-[#85c349]/10 rounded-2xl ${!isPro ? 'right-[96px] lg:right-[144px]' : 'right-0'} z-0`} />

              {/* Green border - on top */}
              <div className={`absolute top-0 w-24 lg:w-36 h-full border-2 border-[#85c349] rounded-2xl ${!isPro ? 'right-[96px] lg:right-[144px]' : 'right-0'} z-20 pointer-events-none`} />

              <div className="flex flex-col gap-0 relative z-10">
                {/* Tier Headers */}
                <div className="flex items-start mb-0">
                  <div className="flex-1" />
                  <div className="w-24 lg:w-36 pt-3 pb-1 flex justify-center items-center">
                    <div className="flex flex-col items-center gap-1">
                      <div className="text-[#2d3748] text-sm font-bold font-['Inter']">Verified</div>
                      <div className="text-[#2d3748] text-xs font-semibold font-['Inter']">Free</div>
                    </div>
                  </div>
                  <div className="w-24 lg:w-36 pt-3 pb-1 flex justify-center items-center">
                    <div className="flex flex-col items-center gap-1">
                      <div className="text-[#2d3748] text-sm font-bold font-['Inter']">Professional</div>
                      <div className="text-[#2d3748] text-xs font-semibold font-['Inter']">${monthlyPrice.toFixed(2)}/month</div>
                    </div>
                  </div>
                </div>

                {/* Features List */}
                <div className="flex flex-col gap-3 relative z-10">
                  {TIER_FEATURES.map((section, sectionIdx) => {
                    // Calculate global row index for zebra striping
                    let globalRowIndex = 0
                    for (let i = 0; i < sectionIdx; i++) {
                      globalRowIndex += TIER_FEATURES[i].features.length
                    }

                    return (
                      <div key={sectionIdx} className="flex flex-col gap-2">
                        {/* Section Title */}
                        <div className={`flex items-center ${sectionIdx === 0 ? 'pb-2 pt-0' : 'py-2'}`}>
                          <div className="flex-1 px-1">
                            <div className="text-[#2d3748] text-sm font-bold font-['Inter']">
                              {section.title}
                            </div>
                          </div>
                          <div className="w-24 lg:w-36" />
                          <div className="w-24 lg:w-36" />
                        </div>

                        {/* Section Features */}
                        <div className="flex flex-col">
                          {section.features.map((feature, featureIdx) => {
                            const currentRowIndex = globalRowIndex + featureIdx
                            const isEvenRow = currentRowIndex % 2 === 0

                            return (
                              <div key={featureIdx} className={`flex items-center mr-0 lg:mr-2 ${isEvenRow ? 'bg-[#ececec] rounded-full' : ''}`}>
                                {/* Feature Name */}
                                <div className="flex-1 px-2 lg:px-4 py-2">
                                  <div className="text-[#2d3748] text-xs font-normal font-['Inter']">
                                    {feature.name}
                                  </div>
                                </div>

                                {/* Verified Column */}
                                <div className="w-24 lg:w-36 flex justify-center items-center py-2 flex-shrink-0">
                                  <div className="w-full flex justify-center items-center">
                                    {typeof feature.verified === 'string' && feature.verified !== 'false' ? (
                                      <div className="text-[#2d3748] text-xs font-medium font-['Inter'] text-center">
                                        {feature.verified}
                                      </div>
                                    ) : (
                                      renderFeatureIcon(feature.verified)
                                    )}
                                  </div>
                                </div>

                                {/* Professional Column */}
                                <div className="w-24 lg:w-36 flex justify-center items-center py-2 flex-shrink-0">
                                  <div className="w-full flex justify-center items-center">
                                    {typeof feature.professional === 'string' && feature.professional !== 'false' ? (
                                      <div className="flex flex-col items-center gap-1">
                                        <div className="text-[#2d3748] text-xs font-medium font-['Inter'] text-center">
                                          {feature.professional}
                                        </div>
                                        {feature.badge && (
                                          <div className="px-2 py-0.5 bg-[#85c349] rounded-[999px] flex justify-center items-center">
                                            <div className="text-white text-[9px] font-bold font-['Inter'] uppercase tracking-wide whitespace-nowrap">
                                              {feature.badge}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      renderFeatureIcon(feature.professional)
                                    )}
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
